import type { FragmentType } from '@packages/gql/generated/shopping';
import { getFragmentData } from '@packages/gql/generated/shopping';
import { useConfig } from '@packages/shared/src/hooks/useConfig/useConfig';
import { SchemaOrgFragmentFragmentDoc } from '@packages/gql/generated/shopping/SchemaOrgFragmentFragmentDoc';
import { getOffersJSON } from './getOffersJSON';
import { getRatingTags } from './getRatingTags';

/* GraphQL */ `
  fragment SchemaOrgFragment on Product {
    name
    ean
    media {
      type
      url
    }
    offers {
      currency
      price
      availability
      sku
    }
    price {
      currency
      value
    }
    brand {
      name
      image
    }
    slug
    akl
    sku
    orderNumber
    availability {
      ...getSchemaAvailabilityFragment
    }
    sellingPoints
    longDescription
    ratingForSchema: seoRating(locale: $locale) {
      product {
        avgRating
        reviewCount
        tags {
          pro {
            tag {
              name
            }
          }
          contra {
            tag {
              name
            }
          }
        }
      }
    }
  }
`;

export const SchemaOrg = ({
  productData,
}: {
  productData: FragmentType<typeof SchemaOrgFragmentFragmentDoc>;
}): string => {
  const {
    offers,
    name,
    media,
    akl,
    brand,
    price,
    ratingForSchema,
    ean,
    longDescription,
    sellingPoints,
    slug,
    availability,
    sku,
  } = getFragmentData(SchemaOrgFragmentFragmentDoc, productData);
  const {
    company,
    host,
    product: { seoImageParams },
  } = useConfig();

  const hostName =
    typeof window !== 'undefined'
      ? `${window?.location?.protocol}//${window?.location?.hostname}`
      : `https://www.${host.domain}`;

  const url = `${hostName}/p/${slug}/${akl}`;

  const aggregateRating =
    ratingForSchema?.product && ratingForSchema.product.reviewCount > 0
      ? {
          '@type': 'AggregateRating',
          ratingValue: ratingForSchema.product.avgRating,
          reviewCount: ratingForSchema.product.reviewCount,
          bestRating: 5,
          worstRating: 1,
        }
      : '';

  const schema = {
    '@context': 'https://schema.org/',
    '@type': 'Product',
    url,
    gtin13: ean,
    name,
    image: media.map(
      (image) => `${image.url}${seoImageParams !== null ? `?${seoImageParams}` : ''}`,
    ),
    description: longDescription || sellingPoints.join('. '),
    sku: akl,
    brand: {
      '@type': 'Brand',
      name: brand?.name || '',
      logo: brand?.image,
    },
    aggregateRating,
    offers: getOffersJSON({
      currentProduct: {
        price,
        akl,
        availability,
        sku,
      },
      companyName: company.name,
      offers,
      url,
    }),
    ...getRatingTags({
      ratingForSchema,
    }),
  };

  return JSON.stringify(schema, null, 2);
};
